import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useTranslate } from '@repo/unicity'
import { useCurrentLanguage } from '@/core/utils'
import { omanFormatDataAtom, omanCountryListAtom, omanSelectedShippingMethodAtom, omanMemberShippingFormAtom } from '../../../stores/oman.store'
import { useRegionsByCountry } from '../../../hooks/useRegionsByCountry'
import { joinAddressLine, getPickupAddressLine, getRegionDisplayName } from '../address-display.helpers'
import { useNavigateToShipping } from '../hooks/useNavigateToShipping'
import { changeLinkButtonSx } from '../payments.styles'

const ShipToSection = () => {
    const { translate } = useTranslate()
    const currentLanguage = useCurrentLanguage()
    const formatData = useAtomValue(omanFormatDataAtom)
    const countryList = useAtomValue(omanCountryListAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const memberShippingForm = useAtomValue(omanMemberShippingFormAtom)
    const goToShipping = useNavigateToShipping()

    const name = formatData?.fullname ?? (formatData?.firstname && formatData?.lastname ? `${formatData.firstname} ${formatData.lastname}` : '')
    const email = formatData?.email ?? ''
    const phone = formatData?.phone ?? ''
    const isPickup = selectedShippingMethod === 'pickup'
    const pickupContactName = memberShippingForm.fullname || name
    const displayName = isPickup ? '' : name

    const addressLine = joinAddressLine([memberShippingForm.address1 || formatData?.address, memberShippingForm.address2 || formatData?.address2])
    const countryCode = memberShippingForm.country || formatData?.country
    const { regions } = useRegionsByCountry(countryCode || 'OM')
    const selectedCountry = countryList.find(item => item.country_code_2 === countryCode)
    const countryDisplayName = selectedCountry
        ? currentLanguage === 'AR' && selectedCountry.name_native
            ? selectedCountry.name_native
            : selectedCountry.name_english
        : countryCode
    const stateDisplayName = getRegionDisplayName(memberShippingForm.state || formatData?.state, regions)
    const cityLine = joinAddressLine([memberShippingForm.city || formatData?.city, stateDisplayName, countryDisplayName])
    const displayAddressLines = isPickup ? [getPickupAddressLine()] : [addressLine, cityLine].filter(Boolean)

    return (
        <Box data-testid="ship-to-section-box">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} data-testid="ship-to-section-header">
                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#003764' }} data-testid="ship-to-section-title">
                    {translate('ship_to')}:
                </Typography>
                <Button
                    size="small"
                    variant="text"
                    onClick={() => goToShipping()}
                    disableRipple
                    sx={changeLinkButtonSx}
                    data-testid="ship-to-section-change-btn">
                    {translate('change')}
                </Button>
            </Stack>
            <Stack spacing={0} data-testid="ship-to-section-address">
                {isPickup && pickupContactName && (
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="ship-to-section-pickup-contact">
                        {pickupContactName}
                    </Typography>
                )}
                {displayName && (
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="ship-to-section-display-name">
                        {displayName}
                    </Typography>
                )}
                {displayAddressLines.map((line, index) => (
                    <Typography
                        key={line}
                        variant="body2"
                        sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }}
                        data-testid={`ship-to-section-address-line-${index}`}>
                        {line}
                    </Typography>
                ))}
            </Stack>
            <Stack mt={2} data-testid="ship-to-section-contact">
                {email && (
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="ship-to-section-email">
                        {email}
                    </Typography>
                )}
                {phone && (
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="ship-to-section-phone">
                        {phone}
                    </Typography>
                )}
            </Stack>
        </Box>
    )
}

export default ShipToSection
